import { MapPin, Loader2, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { EmptyState } from '../shared/EmptyState';
import type { DrillDownProvider } from '../../../types/analytics.types';

type ProviderDrillDownProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateCode: string | null;
  stateName: string;
  providers: DrillDownProvider[];
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

const VettingBadge = ({ status }: { status: string }) => {
  const isApproved = status === 'APPROVED';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isApproved
          ? 'bg-green-100 text-green-700'
          : 'bg-amber-100 text-amber-700'
      }`}
    >
      {isApproved ? (
        <CheckCircle className="w-3 h-3" />
      ) : (
        <Clock className="w-3 h-3" />
      )}
      {isApproved ? 'Approved' : 'Pending'}
    </span>
  );
};

export const ProviderDrillDown = ({
  open,
  onOpenChange,
  stateCode,
  stateName,
  providers,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onLoadMore,
}: ProviderDrillDownProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#3d6560]" />
            {stateName} ({stateCode})
          </SheetTitle>
          <SheetDescription>
            {totalCount.toLocaleString('en-US')} provider
            {totalCount !== 1 ? 's' : ''} in this state
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {!isLoading && providers.length === 0 && (
            <EmptyState message="No providers found for the selected filters." />
          )}

          {providers.map((provider) => (
            <div
              key={provider.id}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900">{provider.name}</p>
                  <p className="text-sm text-gray-500">{provider.companyName}</p>
                </div>
                <VettingBadge status={provider.vettingStatus} />
              </div>

              {provider.city && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                  <MapPin className="w-3 h-3" />
                  {provider.city}
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 mt-2">
                {provider.categories.map((cat) => (
                  <span
                    key={cat}
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#e8f4f2] text-[#3d6560]"
                  >
                    {cat}
                  </span>
                ))}
                {provider.specialties.map((spec) => (
                  <span
                    key={spec}
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          ))}

          {hasNextPage && (
            <div className="pt-2 pb-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  `Load More (${providers.length} of ${totalCount})`
                )}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
